"use client";

import { ArrowRight, Lock, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactChannels = [{
  icon: "mail",
  label: "EMAIL",
  detail: "segreteria.drcolloca@gmail.com"
}, {
  icon: "phone",
  label: "TELEFONO",
  detail: "+39 329 02 14 366"
}, {
  icon: "map",
  label: "STUDIO",
  detail: "Messina - Vibo Valentia"
}];
const inquiryTabs = [{
  value: "ginecologia",
  label: "Ginecologia"
}, {
  value: "ostetricia",
  label: "Ostetricia"
}, {
  value: "forense",
  label: "Forense"
}, {
  value: "altro",
  label: "Altro"
}];
const inquiryOptions = ["Visita Ginecologica", "Consulenza Riproduzione", "Perizia Forense", "Altro"];
function ChannelIcon({
  name
}: {
  name: string;
}) {
  const iconClass = "size-6 text-[color:var(--primary)]";
  if (name === "mail") return <Mail className={iconClass} strokeWidth={1.25} />;
  if (name === "phone") return <Phone className={iconClass} strokeWidth={1.25} />;
  return <MapPin className={iconClass} strokeWidth={1.25} />;
}
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-[color:var(--primary)]/40 bg-card/40 p-10 text-center">
        <p className="font-default text-2xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-[color:var(--primary)]/50 text-foreground hover:bg-[color:var(--primary)]/10 hover:text-foreground">
          Invia un altro messaggio
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <Tabs defaultValue="ginecologia" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-center gap-2 bg-transparent p-0 sm:gap-8">
          {inquiryTabs.map((tab, i) => <TabsTrigger key={tab.value} data-index={i} value={tab.value} className="relative border-0 bg-transparent px-2 pb-3 pt-1 font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground transition-colors data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--primary)] data-[state=active]:shadow-none after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:bg-[color:var(--primary)] after:opacity-0 data-[state=active]:after:opacity-100">
              {tab.label}
            </TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                Nome e Cognome
              </FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" {...field} className="h-12 border-x-0 border-t-0 border-b border-[color:var(--border)] bg-transparent px-0 font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-[color:var(--primary)] focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                Indirizzo Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="mario@esempio.it" {...field} className="h-12 border-x-0 border-t-0 border-b border-[color:var(--border)] bg-transparent px-0 font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-[color:var(--primary)] focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Numero di Telefono
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+39 333 000 0000" {...field} className="h-12 border-x-0 border-t-0 border-b border-[color:var(--border)] bg-transparent px-0 font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-[color:var(--primary)] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Tipo di Richiesta
            </FormLabel>
            <FormControl>
              <select {...field} className="h-12 w-full border-x-0 border-t-0 border-b border-[color:var(--border)] bg-transparent px-0 font-default text-lg text-foreground outline-none focus:border-[color:var(--primary)] focus-visible:ring-0">
                <option value="" className="bg-background text-foreground">
                  Seleziona un'opzione
                </option>
                {inquiryOptions.map((opt, i) => <option key={opt} data-index={i} value={opt} className="bg-background text-foreground">
                    {opt}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
              Messaggio
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Descrivi brevemente la tua richiesta..." {...field} className="min-h-[160px] border-x-0 border-t-0 border-b border-[color:var(--border)] bg-transparent px-0 py-3 font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-[color:var(--primary)] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-center gap-2 pt-2">
        <Lock className="size-3.5 text-[color:var(--primary)]" strokeWidth={1.5} />
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
          I tuoi dati sono trattati con la massima riservatezza
        </p>
      </div>

      {submitError && <div className="border border-[color:var(--destructive)]/40 bg-[color:var(--destructive)]/10 p-4">
          <p className="font-default text-sm text-[color:var(--destructive-foreground)]">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group h-16 w-full bg-[color:var(--primary)] font-mono text-xs tracking-[0.3em] uppercase text-[color:var(--primary-foreground)] hover:bg-[color:var(--primary)]/90">
        <span>{isSubmitting ? "Invio in corso..." : "Invia Messaggio"}</span>
        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.5} />
      </Button>
    </Form>;
}
export default function InviaUnMessaggio() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: channelsRef,
    isVisible: channelsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3179" id="invia-un-messaggio" className="relative overflow-hidden bg-background text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/3 rounded-lg opacity-[0.08] blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-40">
        <div ref={headerRef} className={`mx-auto max-w-4xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[color:var(--primary)]">
            01 &mdash;
          </p>
          <p className="mt-4 font-mono text-[11px] tracking-[0.4em] uppercase text-muted-foreground">
            Contatti
          </p>

          <h2 className="mt-8 font-default text-5xl font-light leading-[0.95] tracking-tight text-foreground sm:text-7xl lg:text-[120px]">
            Invia un{" "}
            <span className="relative inline-block italic text-[color:var(--primary)]">
              Messaggio
              <span aria-hidden="true" className="absolute -bottom-2 left-0 h-px w-full bg-[color:var(--primary)]/70 sm:-bottom-3" />
            </span>
          </h2>

          <p className="mx-auto mt-10 max-w-2xl font-default text-lg italic leading-relaxed text-muted-foreground sm:text-xl">
            Contatta lo studio per prenotare una visita, richiedere una consulenza o per informazioni professionali.
          </p>
        </div>

        <div ref={channelsRef} className={`mt-24 grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--primary)]/20 md:grid-cols-3 transition-all duration-1000 ease-out delay-150 ${channelsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {contactChannels.map((channel, i) => <div key={channel.label} data-index={i} className="relative flex flex-col items-center bg-background/40 px-8 py-12 text-center md:py-14">
              {i < contactChannels.length - 1 && <span aria-hidden="true" className="absolute right-0 top-1/2 hidden h-2/3 w-px -translate-y-1/2 bg-[color:var(--primary)]/25 md:block" />}
              <ChannelIcon name={channel.icon} />
              <p className="mt-6 font-mono text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
                {channel.label}
              </p>
              <p className="mt-3 font-default text-lg text-foreground sm:text-[18px]">
                {channel.detail}
              </p>
            </div>)}
        </div>

        <div ref={formRef} className={`mx-auto mt-24 max-w-3xl transition-all duration-1000 ease-out delay-200 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="border border-[color:var(--primary)]/25 bg-card/30 px-6 py-12 backdrop-blur-sm sm:px-12 sm:py-16">
            <FormProvider formKey="message_form_contacts" formId="message_form_contacts" sectionName="invia-un-messaggio" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 2000
            }
          }} submitText="Invia Messaggio" successMessage="Grazie. Il tuo messaggio è stato ricevuto — ti ricontatteremo al più presto." errorMessage="Non è stato possibile inviare il messaggio. Riprova tra poco.">
              <ReachOutForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}